import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { icons } from 'smartsuite-ui/lib/constants/icons';
import { PadContainer } from 'smartsuite-ui/lib/containers/PadContainer';
import { Button, Fields, Input, VerticalFlex } from 'smartsuite-ui';
import BagSearchResults from './BagSearchResults';
import { useState } from 'react';
import { orderBy } from 'natural-orderby';
import { Title } from 'smartsuite-ui/lib/typography/Title';
import { useTranslation } from 'react-i18next';
import useSkeletonContext from '../skeletonComponents/useSkeletonContext';
import BagSearchResultsSkeleton from '../skeletonComponents/BagSearchResultsSkeleton';

export default function BagSearch(props) {
    const { t } = useTranslation('bagSummary');
    const { id } = useParams();
    const [isClosed, setIsClosed] = useState(false);
    const { showSkeleton, dataLoading } = useSkeletonContext();

    const chevronClassName = isClosed ? icons.chevronRight : icons.chevronLeft;

    const contentClassName = isClosed
        ? 'bag-search__content bag-search__content--closed'
        : 'bag-search__content';

    const isSearchInputValid =
        !props.searchInput ||
        /^(\d{10})?$/.test(props.searchInput.replace(/\s/g, '')) ||
        /^[a-zA-Z0-9]{2,3}\d{6}$/.test(props.searchInput.replace(/\s/g, ''));

    function renderSearchHeader() {
        return (
            <div className="bag-search__content--header">
                <PadContainer cssClassModifiers={['pad-small']}>
                    <Title cssClassModifiers={['size-small']}>Find a Bag</Title>
                </PadContainer>
                <Fields cssClassModifiers={['no-background', 'no-padding']}>
                    <Input
                        cssClassModifiers={['small-relative']}
                        onChange={(event) =>
                            props.setSearchInput(event.target.value)
                        }
                        type="text"
                        placeholder={t('bagTagNumber')}
                        value={props.searchInput}
                        isValid={isSearchInputValid}
                        errorMessage={t('searchErrorMessage')}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                props.onSearch();
                            }
                        }}
                    />
                    <PadContainer cssClassModifiers={['pad-small']}>
                        <Button
                            onClick={props.onSearch}
                            disabled={!isSearchInputValid}
                        >
                            Search
                        </Button>
                    </PadContainer>
                </Fields>
            </div>
        );
    }

    function renderResults() {
        if (showSkeleton) {
            return <BagSearchResultsSkeleton />;
        }

        if (dataLoading) {
            return null;
        }

        if (!props.data?.length) {
            return (
                <div className="bag-search__content-body-error">
                    <Title cssClassModifiers={['size-medium']}>
                        {t('noBagResults')}
                    </Title>
                    {t('noBagTag')}
                </div>
            );
        }

        return orderBy(props.data, (x) => x.bagSelectorResult.createdOn, [
            'desc',
        ]).map((item) => (
            <BagSearchResults
                key={item.bagSelectorResult.id}
                isSelected={item.bagSelectorResult.id === id}
                bagSelectorResult={item.bagSelectorResult}
                flightItinerary={item.bagSelectorResult.flightItinerary}
                onBagSelect={props.onBagSelect}
            />
        ));
    }

    return (
        <div className="bag-search">
            <div className={contentClassName}>
                <div className="bag-search__content-overflow">
                    <PadContainer cssClassModifiers={['pad-small']} />
                    {renderSearchHeader()}
                    <PadContainer cssClassModifiers={['pad-medium']} />
                    {renderResults()}
                </div>
            </div>
            <div
                className="bag-search__rail"
                onClick={() => setIsClosed((prev) => !prev)}
            >
                <span className={chevronClassName}></span>
            </div>
        </div>
    );
}

BagSearch.propTypes = {
    data: PropTypes.instanceOf(Array),
    onBagSelect: PropTypes.func.isRequired,
    onSearch: PropTypes.func.isRequired,
    setSearchInput: PropTypes.func.isRequired,
    searchInput: PropTypes.string,
};
